import { useState, useEffect, useMemo, useRef } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HelpTip } from "../components/HelpTip";
import { Building2, Save, MapPin, FileText, Palette, Upload, Image, Plus, Users, Route, Pencil, Trash2, Layers3, Send, Globe, BellRing } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { useBranding } from "../hooks/useBranding";
import { toast } from "sonner";

const CENTER_TYPES = ["sucursal", "estacion", "oficina", "planta", "corporativo", "operacion"];
const emptyCenterForm = { name: "", center_type: "sucursal", code: "", address: "", status: "active", fiscal_name: "", fiscal_rfc: "", fiscal_address: "", logo_url: "", cost_center_code: "", payroll_series: "" };
const emptyBulkForm = { work_center_id: "", department: "all", employee_ids: [] };

const Company = () => {
  const { user } = useAuth();
  const { fetchBranding } = useBranding();
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [centerDialogOpen, setCenterDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingCenterId, setEditingCenterId] = useState(null);
  const fileInputRef = useRef(null);
  const centerLogoInputRef = useRef(null);
  const [logoUploadingCenterId, setLogoUploadingCenterId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "", rfc: "", address: ""
  });

  const [wlForm, setWlForm] = useState({
    wl_app_name: "", wl_logo_url: "", wl_primary_color: "#059669", wl_secondary_color: "#10b981"
  });
  const [centerForm, setCenterForm] = useState(emptyCenterForm);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [notificationSettings, setNotificationSettings] = useState({ allowed_rule_creator_bands: [], min_rule_creator_rank: 0, default_channels: ["internal_chat"], require_opt_in_for_external: true });
  const [notificationRules, setNotificationRules] = useState([]);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [gupshupConfig, setGupshupConfig] = useState({ waba_name: "", app_name: "", phone_number: "", sandbox: true, credentials: { api_key: "", app_token: "" } });
  const [ruleForm, setRuleForm] = useState({ name: "", event_type: "leave_request_created", channels: ["internal_chat"], audience: { work_center_id: "", department: "" }, message_template: "", nurturing: { enabled: false, schedule_type: "immediate", offset_minutes: 0 } });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [companyRes, centersRes, employeesRes, notifSettingsRes, notifRulesRes, notifLogsRes, gupshupRes] = await Promise.all([
        axios.get(`${API}/company`, { headers, withCredentials: true }),
        axios.get(`${API}/work-centers`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/notification-settings`, { headers, withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/notification-rules`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/notification-logs`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/company/integrations/gupshup`, { headers, withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      if (companyRes.data) {
        setCompany(companyRes.data);
        setFormData({
          name: companyRes.data.name || "",
          rfc: companyRes.data.rfc || "",
          address: companyRes.data.address || ""
        });
        setWlForm({
          wl_app_name: companyRes.data.wl_app_name || "",
          wl_logo_url: companyRes.data.wl_logo_url || "",
          wl_primary_color: companyRes.data.wl_primary_color || "#059669",
          wl_secondary_color: companyRes.data.wl_secondary_color || "#10b981"
        });
      }
      setWorkCenters(centersRes.data || []);
      setEmployees(employeesRes.data || []);
      if (notifSettingsRes.data) setNotificationSettings(notifSettingsRes.data);
      setNotificationRules(notifRulesRes.data || []);
      setNotificationLogs(notifLogsRes.data || []);
      if (gupshupRes.data) setGupshupConfig(previous => ({ ...previous, ...gupshupRes.data }));
    } catch (error) {
      toast.error("Error al cargar datos de la empresa");
    } finally {
      setLoading(false);
    }
  };

  const opts = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company`, formData, opts());
      toast.success("Empresa actualizada");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWL = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company`, wlForm, opts());
      await fetchBranding();
      toast.success("Marca actualizada correctamente");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar marca");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El logo debe ser menor a 2MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/company/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      setWlForm(p => ({ ...p, wl_logo_url: res.data.wl_logo_url }));
      await fetchBranding();
      toast.success("Logo subido correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.put(`${API}/company`, { wl_logo_url: "" }, opts());
      setWlForm(p => ({ ...p, wl_logo_url: "" }));
      await fetchBranding();
      toast.success("Logo eliminado");
    } catch {
      toast.error("Error al eliminar logo");
    }
  };

  const departments = useMemo(() => [...new Set(employees.map(employee => employee.department).filter(Boolean))], [employees]);
  const hierarchyBands = useMemo(() => [...new Set(employees.map(employee => employee.hierarchy_band_name).filter(Boolean))], [employees]);
  const bulkEmployees = useMemo(() => {
    if (bulkForm.department === "all") return employees;
    return employees.filter(employee => employee.department === bulkForm.department);
  }, [bulkForm.department, employees]);

  const handleOpenCreateCenter = () => {
    setEditingCenterId(null);
    setCenterForm(emptyCenterForm);
    setCenterDialogOpen(true);
  };

  const handleOpenEditCenter = (center) => {
    setEditingCenterId(center.work_center_id);
    setCenterForm({
      name: center.name || "",
      center_type: center.center_type || "sucursal",
      code: center.code || "",
      address: center.address || "",
      status: center.status || "active",
      fiscal_name: center.fiscal_name || center.name || "",
      fiscal_rfc: center.fiscal_rfc || "",
      fiscal_address: center.fiscal_address || center.address || "",
      logo_url: center.logo_url || "",
      cost_center_code: center.cost_center_code || "",
      payroll_series: center.payroll_series || ""
    });
    setCenterDialogOpen(true);
  };

  const handleCenterLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editingCenterId) return;
    setLogoUploadingCenterId(editingCenterId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/work-centers/${editingCenterId}/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      setCenterForm(previous => ({ ...previous, logo_url: response.data.logo_url }));
      toast.success("Logo del centro actualizado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo subir el logo del centro");
    } finally {
      setLogoUploadingCenterId(null);
    }
  };

  const handleSaveCenter = async () => {
    if (!centerForm.name) {
      toast.error("El nombre del centro es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editingCenterId) {
        await axios.put(`${API}/work-centers/${editingCenterId}`, centerForm, opts());
        toast.success("Centro de trabajo actualizado");
      } else {
        await axios.post(`${API}/work-centers`, centerForm, opts());
        toast.success("Centro de trabajo creado");
      }
      setCenterDialogOpen(false);
      setCenterForm(emptyCenterForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar el centro");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCenter = async (workCenterId) => {
    try {
      await axios.delete(`${API}/work-centers/${workCenterId}`, opts());
      toast.success("Centro de trabajo eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el centro");
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkForm.work_center_id || bulkForm.employee_ids.length === 0) {
      toast.error("Selecciona un centro y al menos un colaborador");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/employees/bulk-assign-work-center`, bulkForm, opts());
      toast.success("Centro asignado al grupo seleccionado");
      setBulkDialogOpen(false);
      setBulkForm(emptyBulkForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo realizar la asignación masiva");
    } finally {
      setSaving(false);
    }
  };

  const toggleListValue = (value, current, setter) => {
    setter(current.includes(value) ? current.filter(item => item !== value) : [...current, value]);
  };

  const handleSaveNotificationSettings = async () => {
    try {
      await axios.post(`${API}/notification-settings`, notificationSettings, opts());
      toast.success("Política de notificaciones guardada");
      fetchData();
    } catch {
      toast.error("No se pudo guardar la política");
    }
  };

  const handleSaveGupshup = async () => {
    try {
      await axios.post(`${API}/company/integrations/gupshup`, gupshupConfig, opts());
      toast.success("Configuración Gupshup guardada");
      fetchData();
    } catch {
      toast.error("No se pudo guardar Gupshup");
    }
  };

  const handleSaveRule = async () => {
    try {
      await axios.post(`${API}/notification-rules`, ruleForm, opts());
      toast.success("Regla guardada");
      setRuleForm({ name: "", event_type: "leave_request_created", channels: ["internal_chat"], audience: { work_center_id: "", department: "" }, message_template: "", nurturing: { enabled: false, schedule_type: "immediate", offset_minutes: 0 } });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar la regla");
    }
  };

  if (loading) {
    return (
      <Layout title="Mi Empresa">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const isWhiteLabel = company?.white_label === true;
  const assignedEmployees = employees.filter(employee => employee.work_center_id).length;

  return (
    <Layout title="Mi Empresa" subtitle="Configura datos generales, centros de trabajo y experiencia de marca">
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="border-slate-200 lg:col-span-2 bg-gradient-to-br from-white to-sky-50/70">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-sky-600 text-white flex items-center justify-center shrink-0">
              <Layers3 className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Operación multisede lista</h2>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Define sucursales, estaciones, oficinas o plantas para conectar estructura, asistencia, desempeño y liderazgo real.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge className="bg-sky-100 text-sky-700">Centros de trabajo</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">Asignación masiva</Badge>
                <Badge className="bg-violet-100 text-violet-700">Listo para organigrama y desempeño</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Centros</p>
                <p className="text-2xl font-bold text-slate-900">{workCenters.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Asignados</p>
                <p className="text-2xl font-bold text-slate-900">{assignedEmployees}</p>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-2">
              <Users className="w-4 h-4 mt-0.5 text-emerald-500" />
              <p>{assignedEmployees} de {employees.length} colaboradores ya tienen centro principal.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="flex-wrap h-auto gap-1" data-testid="company-tabs">
          <TabsTrigger value="general" data-testid="company-tab-general">Empresa</TabsTrigger>
          <TabsTrigger value="centers" data-testid="company-tab-centers">Centros de trabajo</TabsTrigger>
          <TabsTrigger value="automation" data-testid="company-tab-automation">Automatizaciones</TabsTrigger>
          {isWhiteLabel && <TabsTrigger value="branding" data-testid="company-tab-branding">White Label</TabsTrigger>}
        </TabsList>

        <TabsContent value="general">
          <div className="max-w-4xl space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Información de la Empresa
                  <HelpTip title="Datos base de la empresa">
                    <p>Estos datos forman la cabecera legal y operativa de tu cuenta.</p>
                    <p>La configuración de centros vive en la pestaña siguiente.</p>
                  </HelpTip>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Empresa</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Mi Empresa S.A. de C.V." data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rfc" className="flex items-center gap-2"><FileText className="w-4 h-4" />RFC</Label>
                  <Input id="rfc" value={formData.rfc} onChange={(e) => setFormData(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} placeholder="AAA010101AAA" maxLength={13} data-testid="company-rfc-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="flex items-center gap-2"><MapPin className="w-4 h-4" />Dirección Fiscal</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Calle, Número, Colonia, CP, Ciudad" data-testid="company-address-input" />
                </div>
                {company && (
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Información del plan</h4>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm text-slate-500">Plan Actual</p><p className="font-semibold text-slate-900 capitalize">{company.plan}</p></div>
                      <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm text-slate-500">Máx. Empleados</p><p className="font-semibold text-slate-900">{company.max_employees}</p></div>
                      <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm text-slate-500">Estado</p><p className="font-semibold text-slate-900 capitalize">{company.plan_status}</p></div>
                      <div className="p-3 rounded-lg bg-slate-50"><p className="text-sm text-slate-500">ID Empresa</p><p className="font-mono text-xs text-slate-600">{company.company_id}</p></div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="save-company-btn">
                    {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Guardar Cambios</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="centers">
          <div className="grid xl:grid-cols-[1.25fr_0.95fr] gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Route className="w-5 h-5 text-sky-600" />
                    Centros de Trabajo
                    <HelpTip title="Centros de trabajo">
                      <p>Úsalos para modelar sucursales, estaciones, oficinas, plantas o centros operativos.</p>
                      <p>Se reflejan en empleados, asistencia, organigrama y desempeño.</p>
                    </HelpTip>
                  </CardTitle>
                  <Button onClick={handleOpenCreateCenter} className="bg-sky-600 hover:bg-sky-700" data-testid="add-work-center-btn"><Plus className="w-4 h-4 mr-2" />Nuevo centro</Button>
                </div>
              </CardHeader>
              <CardContent>
                {workCenters.length === 0 ? (
                  <div className="text-center py-14 text-slate-500" data-testid="work-centers-empty-state">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Aún no has registrado centros</p>
                    <p className="text-sm mt-1">Empieza con una sucursal, estación, oficina o planta.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4" data-testid="work-centers-grid">
                    {workCenters.map(center => (
                      <Card key={center.work_center_id} className="border-slate-200 shadow-sm">
                        <CardContent className="p-5 space-y-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-bold text-slate-900">{center.name}</p>
                              <p className="text-sm text-slate-500 capitalize">{center.center_type}</p>
                            </div>
                            {center.logo_url ? <img src={center.logo_url} alt={center.name} className="h-10 w-auto max-w-[72px] object-contain rounded-lg border border-slate-200 bg-white p-1" data-testid={`work-center-logo-${center.work_center_id}`} /> : null}
                            <Badge className={center.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>{center.status === "active" ? "Activo" : "Inactivo"}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                              <p className="text-xs text-slate-400 uppercase tracking-wide">Código</p>
                              <p className="font-semibold text-slate-900">{center.code || "—"}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                              <p className="text-xs text-slate-400 uppercase tracking-wide">Colaboradores</p>
                              <p className="font-semibold text-slate-900">{center.employee_count || 0}</p>
                            </div>
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{center.address || "Sin dirección registrada"}</p>
                            {center.fiscal_rfc && <p className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />RFC fiscal: {center.fiscal_rfc}</p>}
                            <p className="flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" />{center.geofence_count || 0} geofence(s) vinculados</p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => handleOpenEditCenter(center)} data-testid={`edit-work-center-${center.work_center_id}`}><Pencil className="w-4 h-4 mr-2" />Editar</Button>
                            <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteCenter(center.work_center_id)} data-testid={`delete-work-center-${center.work_center_id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" />Asignación masiva</CardTitle>
                  <Button variant="outline" onClick={() => setBulkDialogOpen(true)} data-testid="open-bulk-assign-dialog-btn">Asignar grupo</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">Asigna un centro principal a un grupo de personas para que asistencia, organigrama y desempeño trabajen con la misma estructura.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Centros activos</p>
                    <p className="text-2xl font-bold text-slate-900">{workCenters.filter(center => center.status === "active").length}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Sin asignar</p>
                    <p className="text-2xl font-bold text-slate-900">{employees.length - assignedEmployees}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="automation">
          <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><BellRing className="w-5 h-5 text-sky-600" />Política raíz de notificaciones</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Rango mínimo para crear reglas</Label><Input type="number" min="0" value={notificationSettings.min_rule_creator_rank || 0} onChange={e => setNotificationSettings(p => ({ ...p, min_rule_creator_rank: Number(e.target.value || 0) }))} data-testid="notification-min-rank-input" /></div>
                    <div className="space-y-2"><Label>Opt-in obligatorio externos</Label><Select value={String(notificationSettings.require_opt_in_for_external)} onValueChange={value => setNotificationSettings(p => ({ ...p, require_opt_in_for_external: value === 'true' }))}><SelectTrigger data-testid="notification-optin-required-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Sí</SelectItem><SelectItem value="false">No</SelectItem></SelectContent></Select></div>
                  </div>
                  <div className="space-y-2"><Label>Bandas facultadas para crear reglas</Label><div className="grid md:grid-cols-2 gap-2 p-3 rounded-xl border border-slate-200" data-testid="notification-creator-bands-list">{hierarchyBands.length === 0 ? <p className="text-sm text-slate-500">No hay bandas capturadas todavía.</p> : hierarchyBands.map(band => <label key={band} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={(notificationSettings.allowed_rule_creator_bands || []).includes(band)} onChange={() => toggleListValue(band, notificationSettings.allowed_rule_creator_bands || [], next => setNotificationSettings(p => ({ ...p, allowed_rule_creator_bands: next })))} data-testid={`notification-band-checkbox-${band}`} />{band}</label>)}</div></div>
                  <Button onClick={handleSaveNotificationSettings} className="bg-sky-600 hover:bg-sky-700" data-testid="save-notification-settings-btn"><Save className="w-4 h-4 mr-2" />Guardar política</Button>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="w-5 h-5 text-emerald-600" />Credenciales Gupshup por cliente</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>WABA</Label><Input value={gupshupConfig.waba_name || ""} onChange={e => setGupshupConfig(p => ({ ...p, waba_name: e.target.value }))} data-testid="company-gupshup-waba-input" /></div>
                    <div><Label>App name</Label><Input value={gupshupConfig.app_name || ""} onChange={e => setGupshupConfig(p => ({ ...p, app_name: e.target.value }))} data-testid="company-gupshup-app-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Teléfono WABA</Label><Input value={gupshupConfig.phone_number || ""} onChange={e => setGupshupConfig(p => ({ ...p, phone_number: e.target.value }))} data-testid="company-gupshup-phone-input" /></div>
                    <div><Label>API Key</Label><Input type="password" value={gupshupConfig.credentials?.api_key || ""} onChange={e => setGupshupConfig(p => ({ ...p, credentials: { ...p.credentials, api_key: e.target.value } }))} data-testid="company-gupshup-api-key-input" /></div>
                  </div>
                  <Button onClick={handleSaveGupshup} data-testid="company-save-gupshup-btn">Guardar Gupshup</Button>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Send className="w-5 h-5 text-violet-600" />Programación de nurturing / reglas</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>Nombre de regla</Label><Input value={ruleForm.name} onChange={e => setRuleForm(p => ({ ...p, name: e.target.value }))} data-testid="company-rule-name-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Evento</Label><Select value={ruleForm.event_type} onValueChange={value => setRuleForm(p => ({ ...p, event_type: value }))}><SelectTrigger data-testid="company-rule-event-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="leave_request_created">Solicitud de permiso</SelectItem><SelectItem value="leave_request_approved">Permiso aprobado</SelectItem><SelectItem value="attendance_overtime_requested">Horas extra solicitadas</SelectItem><SelectItem value="performance_evaluation_created">Evaluación creada</SelectItem><SelectItem value="performance_evaluation_completed">Evaluación completada</SelectItem><SelectItem value="payroll_batch_created">Lote nómina</SelectItem><SelectItem value="vacancy_request_created">Solicitud vacante</SelectItem><SelectItem value="review_task_assigned">Shortlist asignada</SelectItem><SelectItem value="helpdesk_ticket_created">Ticket helpdesk</SelectItem><SelectItem value="complaint_created">Denuncia</SelectItem><SelectItem value="onboarding_started">Onboarding iniciado</SelectItem></SelectContent></Select></div>
                    <div><Label>Modo de nurturing</Label><Select value={ruleForm.nurturing.schedule_type} onValueChange={value => setRuleForm(p => ({ ...p, nurturing: { ...p.nurturing, enabled: true, schedule_type: value } }))}><SelectTrigger data-testid="company-rule-schedule-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="immediate">Inmediato</SelectItem><SelectItem value="delayed">Diferido</SelectItem><SelectItem value="digest">Resumen</SelectItem></SelectContent></Select></div>
                  </div>
                  <div><Label>Plantilla de mensaje</Label><Textarea rows={3} value={ruleForm.message_template} onChange={e => setRuleForm(p => ({ ...p, message_template: e.target.value }))} data-testid="company-rule-template-input" /></div>
                  <div className="flex gap-4 text-sm">{['email','whatsapp','internal_chat'].map(channel => <label key={channel} className="flex items-center gap-2"><input type="checkbox" checked={ruleForm.channels.includes(channel)} onChange={() => setRuleForm(p => ({ ...p, channels: p.channels.includes(channel) ? p.channels.filter(item => item !== channel) : [...p.channels, channel] }))} data-testid={`company-rule-channel-${channel}`} />{channel}</label>)}</div>
                  <Button onClick={handleSaveRule} className="bg-violet-600 hover:bg-violet-700" data-testid="company-save-rule-btn">Guardar regla</Button>
                  <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="company-rules-list">{notificationRules.map(rule => <div key={rule.rule_id} className="p-3 rounded-xl bg-slate-50 border border-slate-200"><p className="font-medium text-slate-900 text-sm">{rule.name}</p><p className="text-xs text-slate-500">{rule.event_type} · {rule.channels.join(', ')} · {rule.nurturing?.schedule_type || 'immediate'}</p></div>)}</div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-lg">Bitácora rápida</CardTitle></CardHeader>
                <CardContent className="space-y-2 max-h-60 overflow-y-auto" data-testid="company-notification-logs-list">{notificationLogs.slice(0, 15).map(log => <div key={log.log_id} className="p-3 rounded-xl bg-slate-50 border border-slate-200"><div className="flex items-center justify-between gap-3"><p className="font-medium text-slate-900 text-sm">{log.channel} · {log.status}</p><span className="text-[10px] text-slate-400">{new Date(log.created_at).toLocaleString('es-MX')}</span></div><p className="text-xs text-slate-500">{log.event_type} → {log.recipient_name}</p></div>)}</CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {isWhiteLabel && (
          <TabsContent value="branding">
            <div className="max-w-4xl">
              <Card className="border-violet-200 bg-gradient-to-br from-white to-violet-50/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-violet-600" />
                    Tu Marca (White Label)
                  </CardTitle>
                  <p className="text-sm text-slate-500">Personaliza el sistema con tu identidad visual. Tus empleados verán tu marca, no Human-OZ.</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label>Logotipo</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-white overflow-hidden" data-testid="wl-logo-preview">
                        {wlForm.wl_logo_url ? (
                          <img src={wlForm.wl_logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                        ) : (
                          <Image className="w-8 h-8 text-slate-300" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} data-testid="wl-logo-file-input" />
                        <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo} data-testid="wl-upload-logo-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                          {uploadingLogo ? <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          {uploadingLogo ? "Subiendo..." : "Subir Logo"}
                        </Button>
                        {wlForm.wl_logo_url && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={handleRemoveLogo} data-testid="wl-remove-logo-btn">
                            Eliminar logo
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wl_app_name">Nombre de la Aplicación</Label>
                    <Input id="wl_app_name" value={wlForm.wl_app_name} onChange={(e) => setWlForm(p => ({ ...p, wl_app_name: e.target.value }))} placeholder="Mi Sistema RH" data-testid="wl-app-name-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="wl_primary">Color Primario</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" id="wl_primary" value={wlForm.wl_primary_color} onChange={(e) => setWlForm(p => ({ ...p, wl_primary_color: e.target.value }))} className="w-10 h-10 rounded-lg border border-slate-300 cursor-pointer" data-testid="wl-primary-color" />
                        <Input value={wlForm.wl_primary_color} onChange={(e) => setWlForm(p => ({ ...p, wl_primary_color: e.target.value }))} className="flex-1 font-mono text-sm" data-testid="wl-primary-color-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wl_secondary">Color Secundario</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" id="wl_secondary" value={wlForm.wl_secondary_color} onChange={(e) => setWlForm(p => ({ ...p, wl_secondary_color: e.target.value }))} className="w-10 h-10 rounded-lg border border-slate-300 cursor-pointer" data-testid="wl-secondary-color" />
                        <Input value={wlForm.wl_secondary_color} onChange={(e) => setWlForm(p => ({ ...p, wl_secondary_color: e.target.value }))} className="flex-1 font-mono text-sm" data-testid="wl-secondary-color-input" />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-200 bg-white">
                    <p className="text-xs font-medium text-slate-400 mb-3">VISTA PREVIA DEL MENÚ</p>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                      {wlForm.wl_logo_url ? (
                        <img src={wlForm.wl_logo_url} alt="Preview" className="h-8 w-auto max-w-[120px] object-contain" />
                      ) : (
                        <span className="text-lg font-bold" style={{ color: wlForm.wl_primary_color }}>{wlForm.wl_app_name || "Mi Empresa"}</span>
                      )}
                      {wlForm.wl_logo_url && wlForm.wl_app_name && (
                        <span className="text-sm font-bold text-slate-800">{wlForm.wl_app_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSaveWL} className="bg-violet-600 hover:bg-violet-700" disabled={saving} data-testid="save-wl-btn">
                      {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Palette className="w-4 h-4 mr-2" />Guardar Marca</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={centerDialogOpen} onOpenChange={setCenterDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCenterId ? "Editar centro de trabajo" : "Nuevo centro de trabajo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={centerForm.name} onChange={(e) => setCenterForm(previous => ({ ...previous, name: e.target.value }))} placeholder="Sucursal Norte" data-testid="work-center-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Razón fiscal</Label><Input value={centerForm.fiscal_name} onChange={(e) => setCenterForm(previous => ({ ...previous, fiscal_name: e.target.value }))} placeholder="Operadora Norte SA de CV" data-testid="work-center-fiscal-name-input" /></div>
              <div className="space-y-2"><Label>RFC fiscal</Label><Input value={centerForm.fiscal_rfc} onChange={(e) => setCenterForm(previous => ({ ...previous, fiscal_rfc: e.target.value.toUpperCase() }))} placeholder="AAA010101AAA" data-testid="work-center-fiscal-rfc-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={centerForm.center_type} onValueChange={(value) => setCenterForm(previous => ({ ...previous, center_type: value }))}>
                  <SelectTrigger data-testid="work-center-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CENTER_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Código</Label>
                <Input value={centerForm.code} onChange={(e) => setCenterForm(previous => ({ ...previous, code: e.target.value }))} placeholder="NTE-01" data-testid="work-center-code-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Centro de costo</Label><Input value={centerForm.cost_center_code} onChange={(e) => setCenterForm(previous => ({ ...previous, cost_center_code: e.target.value }))} placeholder="CC-NORTE" data-testid="work-center-cost-center-code-input" /></div>
              <div className="space-y-2"><Label>Serie nómina</Label><Input value={centerForm.payroll_series} onChange={(e) => setCenterForm(previous => ({ ...previous, payroll_series: e.target.value }))} placeholder="NTE" data-testid="work-center-payroll-series-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input value={centerForm.address} onChange={(e) => setCenterForm(previous => ({ ...previous, address: e.target.value }))} placeholder="Av. Reforma 123, CDMX" data-testid="work-center-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Dirección fiscal</Label>
              <Input value={centerForm.fiscal_address} onChange={(e) => setCenterForm(previous => ({ ...previous, fiscal_address: e.target.value }))} placeholder="Dirección fiscal del centro" data-testid="work-center-fiscal-address-input" />
            </div>
            {editingCenterId && <div className="space-y-2">
              <Label>Logotipo del centro</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-20 h-20 rounded-xl border border-dashed border-slate-300 bg-white flex items-center justify-center overflow-hidden">{centerForm.logo_url ? <img src={centerForm.logo_url} alt="Logo centro" className="w-full h-full object-contain p-1" /> : <Image className="w-6 h-6 text-slate-300" />}</div>
                <div>
                  <input type="file" ref={centerLogoInputRef} className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleCenterLogoUpload} data-testid="work-center-logo-file-input" />
                  <Button type="button" variant="outline" onClick={() => centerLogoInputRef.current?.click()} disabled={logoUploadingCenterId === editingCenterId} data-testid="work-center-upload-logo-btn">{logoUploadingCenterId === editingCenterId ? "Subiendo..." : "Subir logo del centro"}</Button>
                </div>
              </div>
            </div>}
            <div className="space-y-2">
              <Label>Estatus</Label>
              <Select value={centerForm.status} onValueChange={(value) => setCenterForm(previous => ({ ...previous, status: value }))}>
                <SelectTrigger data-testid="work-center-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveCenter} className="bg-sky-600 hover:bg-sky-700" data-testid="save-work-center-btn">Guardar centro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Asignación masiva por grupo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Centro destino</Label>
                <Select value={bulkForm.work_center_id} onValueChange={(value) => setBulkForm(previous => ({ ...previous, work_center_id: value }))}>
                  <SelectTrigger data-testid="bulk-work-center-select"><SelectValue placeholder="Selecciona un centro" /></SelectTrigger>
                  <SelectContent>
                    {workCenters.map(center => <SelectItem key={center.work_center_id} value={center.work_center_id}>{center.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Departamento</Label>
                <Select value={bulkForm.department} onValueChange={(value) => setBulkForm(previous => ({ ...previous, department: value, employee_ids: [] }))}>
                  <SelectTrigger data-testid="bulk-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {departments.map(department => <SelectItem key={department} value={department}>{department}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Colaboradores</Label>
              <div className="max-h-56 overflow-y-auto border rounded-xl p-3 space-y-2" data-testid="bulk-employee-list">
                {bulkEmployees.map(employee => (
                  <label key={employee.employee_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkForm.employee_ids.includes(employee.employee_id)}
                      onChange={(event) => {
                        const employeeIds = event.target.checked
                          ? [...bulkForm.employee_ids, employee.employee_id]
                          : bulkForm.employee_ids.filter(id => id !== employee.employee_id);
                        setBulkForm(previous => ({ ...previous, employee_ids: employeeIds }));
                      }}
                      data-testid={`bulk-employee-checkbox-${employee.employee_id}`}
                    />
                    <span className="text-sm text-slate-700">{employee.first_name} {employee.last_name} — {employee.department}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleBulkAssign} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-assign-submit-btn">Aplicar asignación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Company;
